import React, { createContext, useContext, useMemo, ReactNode } from "react";
import { PERMISSIONS } from "@/config";
import { UserPermissions } from "@/services/user-permissions/user-permissions";

export interface SessionContextContract {
  username: string | null;
  isAuthenticated: boolean;
  permissions: PERMISSIONS[];
  hasPermission: (permission: string) => boolean;
}

const SessionContext = createContext<SessionContextContract>({
  username: null,
  isAuthenticated: false,
  permissions: [],
  hasPermission: () => false,
});

export const useSession = () => useContext(SessionContext);

interface SessionProviderProps {
  children: ReactNode;
}

export const SessionProvider: React.FC<SessionProviderProps> = ({ children }) => {
  const userPermissions = UserPermissions.current;
  const permissions = userPermissions.permissions;

  const value = useMemo<SessionContextContract>(
    () => ({
      username: userPermissions.username,
      isAuthenticated: userPermissions.isAuth,
      permissions,
      hasPermission: (permission: string) => permissions.includes(permission as PERMISSIONS),
    }),
    [userPermissions.username, userPermissions.isAuth, permissions]
  );

  return <SessionContext.Provider value={value}>{children}</SessionContext.Provider>;
};
